import React from 'react';
import '../styles/legal.css';

const Cookies = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <h1 className="legal-title">ADGRO COOKIE POLICY</h1>

        <div className="legal-meta">
          <p><strong>Effective Date:</strong> January 18, 2026</p>
          <p><strong>Last Updated:</strong> January 18, 2026</p>
        </div>

        <p className="legal-intro">
          This Cookie Policy explains how Rhombus Labs Inc. ("Rhombus Labs", "we", "us", or "our") uses cookies, pixels, and similar tracking technologies on our website and through our Adgro advertising platform. Adgro is a product developed and operated by Rhombus Labs Inc.
        </p>

        <section className="legal-section">
          <h2>1. WHAT ARE COOKIES?</h2>
          <p>Cookies are small text files stored on your device when you visit a website. They help websites remember your preferences, understand how you use the site, and enable certain features. Similar technologies include pixels (small images embedded in web pages), web beacons, and local storage.</p>
        </section>

        <section className="legal-section">
          <h2>2. HOW WE USE COOKIES</h2>
          <p>Adgro uses cookies and similar technologies for the following purposes:</p>

          <h3>2.1 Essential Cookies</h3>
          <p>These cookies are necessary for the website to function and cannot be switched off. They include cookies that:</p>
          <ul>
            <li>Enable you to log in and access your account</li>
            <li>Remember your preferences and settings</li>
            <li>Maintain security and prevent fraud</li>
          </ul>

          <h3>2.2 Performance and Analytics Cookies</h3>
          <p>These cookies help us understand how visitors interact with our website by collecting information such as:</p>
          <ul>
            <li>Pages visited and time spent on each page</li>
            <li>Error messages and loading times</li>
            <li>How you arrived at our website</li>
            <li>Aggregate usage statistics</li>
          </ul>

          <h3>2.3 Advertising and Targeting Cookies</h3>
          <p>These cookies are used to deliver relevant advertisements and measure their effectiveness. They are central to Adgro's advertising platform and are used to:</p>
          <ul>
            <li>Track browsing behavior across websites</li>
            <li>Build audience profiles based on interests and behavior</li>
            <li>Deliver personalized advertisements</li>
            <li>Measure ad impressions, clicks, and conversions</li>
            <li>Limit the number of times you see an advertisement</li>
            <li>Attribute conversions to specific advertising campaigns</li>
          </ul>

          <h3>2.4 Functional Cookies</h3>
          <p>These cookies enable enhanced functionality and personalization, such as remembering your language preferences or region.</p>
        </section>

        <section className="legal-section">
          <h2>3. TYPES OF COOKIES WE USE</h2>
          <p>The following table provides more detail about the cookies we use:</p>
          <div className="legal-table-container">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Cookie Name</th>
                  <th>Purpose</th>
                  <th>Duration</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>_adgro_session</td>
                  <td>Session management</td>
                  <td>Session</td>
                  <td>Essential</td>
                </tr>
                <tr>
                  <td>_adgro_uid</td>
                  <td>User identification for targeting</td>
                  <td>1 year</td>
                  <td>Advertising</td>
                </tr>
                <tr>
                  <td>_ga</td>
                  <td>Google Analytics</td>
                  <td>2 years</td>
                  <td>Analytics</td>
                </tr>
                <tr>
                  <td>cookie_consent</td>
                  <td>Stores consent preferences</td>
                  <td>1 year</td>
                  <td>Essential</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="legal-section">
          <h2>4. THIRD-PARTY COOKIES</h2>
          <p>Our website and advertising platform may include cookies from third-party services, including:</p>
          <ul>
            <li><strong>Google Analytics:</strong> For website analytics and performance measurement</li>
            <li><strong>Ad Network Partners:</strong> For ad delivery and measurement</li>
            <li><strong>Payment Processors:</strong> For secure payment processing</li>
          </ul>
          <p>These third parties have their own privacy policies governing how they use information collected through their cookies.</p>
        </section>

        <section className="legal-section">
          <h2>5. ONLINE BEHAVIORAL ADVERTISING</h2>
          <p>Adgro's advertising platform uses cookies and similar technologies to enable online behavioral advertising ("OBA"). This means:</p>
          <ul>
            <li>We collect data about browsing behavior across websites</li>
            <li>We use this data to create audience segments and interest profiles</li>
            <li>We deliver advertisements based on inferred interests</li>
            <li>We measure the effectiveness of advertising campaigns</li>
          </ul>
          <p>Our retail Customers are responsible for providing appropriate notices to their end consumers about these practices and obtaining any required consents.</p>
        </section>

        <section className="legal-section">
          <h2>6. YOUR CHOICES AND CONSENT</h2>
          <p>You have choices about how cookies are used:</p>

          <h3>6.1 Cookie Consent Banner</h3>
          <p>When you first visit our website, you will see a cookie consent banner that allows you to accept or manage your cookie preferences.</p>

          <h3>6.2 Browser Settings</h3>
          <p>Most web browsers allow you to control cookies through their settings. You can typically set your browser to refuse cookies, delete cookies, or alert you when cookies are being sent. Note that blocking some cookies may affect website functionality.</p>

          <h3>6.3 Opt-Out of Targeted Advertising</h3>
          <p>You can opt out of targeted advertising through:</p>
          <ul>
            <li><strong>Digital Advertising Alliance of Canada (DAAC):</strong> youradchoices.ca</li>
            <li><strong>Network Advertising Initiative:</strong> optout.networkadvertising.org</li>
            <li>Your browser's "Do Not Track" settings</li>
          </ul>
          <p>Note that opting out does not mean you will no longer see advertisements, only that the advertisements you see may be less relevant to your interests.</p>

          <h3>6.4 Mobile Device Settings</h3>
          <p>Mobile devices provide settings to limit ad tracking. Consult your device's settings for options to reset your advertising identifier or opt out of personalized ads.</p>
        </section>

        <section className="legal-section">
          <h2>7. DATA RETENTION</h2>
          <p>Cookie data is retained for the duration specified in the cookies table above. After expiration, cookies are automatically deleted. We retain associated analytics data in aggregated, anonymized form.</p>
        </section>

        <section className="legal-section">
          <h2>8. CONSENT RECORDS</h2>
          <p>We maintain records of consent preferences as required by applicable law. This includes the date and time of consent, the specific choices made, and any subsequent changes.</p>
        </section>

        <section className="legal-section">
          <h2>9. CHANGES TO THIS POLICY</h2>
          <p>We may update this Cookie Policy from time to time. Material changes will be communicated through our website. We encourage you to review this policy periodically.</p>
        </section>

        <section className="legal-section">
          <h2>10. CONTACT US</h2>
          <p>For questions about this Cookie Policy or our use of cookies, please contact:</p>
          <div className="legal-contact">
            <p><strong>Rhombus Labs Inc.</strong></p>
            <p>Email: <a href="mailto:support@adgro.ca">support@adgro.ca</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Cookies;
